import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { RootState } from 'typesafe-actions'

import { Box, Flex } from '@driftt/dds'

import { AccountActivityUserAvatar } from 'components/Avatars'
import MobileTooltipQR from 'components/MobileTooltipQR'
import SaveFilterModal from 'components/SaveFilterModal'
import { driftLoadingStateBuilder } from 'components/States'
import Toast from 'components/Toast'
import { PresenceContext } from 'context/presenceContext'
import { ScrollContext } from 'context/scrollContext'
import usePresenceStore from 'hooks/usePresenceStore'
import { useAppSelector } from 'hooks/useRedux'
import useSavedFilterEditModal from 'hooks/useSavedFilterEditModal'
import useSavedFilters, { INACTIVE_FILTER } from 'hooks/useSavedFilters'
import { SessionMetrics } from 'hooks/useSessionMetrics'
import withNotificationsModal from 'notifications/hooks/withNotificationsModal'
import CCRMFeed from 'sections/CCRM/Feed'
import CCRMFeedbackModal from 'sections/CCRM/FeedbackModal'
import CCRMHeaderBar from 'sections/CCRM/HeaderBar'
import CCRMSavedViewsSidebar from 'sections/CCRM/SavedFiltersSidebar'
import { CurrentUser } from 'typings/currentUser'
import { getDisplayName, getUserDisplayRole } from 'utils/user'

import { getCurrentOrg, getCurrentUser } from './selectors'

import styles from './styles.module.css'

const ENGAGED_SESSIONS_KEY = 'ccrm.feed.engagedSessions'
const ENGAGED_SESSION_SCROLL_THRESHOLD = 500
const ENGAGED_SESSION_COUNT_FEEDBACK_THRESHOLD = 3

interface UserCardProps {
  currentUser: CurrentUser | undefined
  currentOrg: any
}

interface CCRMActivityFeedPageProps {
  sessionMetrics: SessionMetrics
  hasSidebar?: boolean
}

const UserCard: React.FC<UserCardProps> = ({ currentUser, currentOrg }) => {
  const userPosition = getUserDisplayRole(currentUser)

  return (
    <Box className={styles.userCard}>
      <Box className={styles.cardAvatar}>
        <Flex justifyContent="center" alignItems="center" css={{ width: '100%' }}>
          <AccountActivityUserAvatar
            size="large"
            imageUrl={currentUser?.avatarUrl ?? ''}
            name={currentUser?.name || currentUser?.email}
          />
        </Flex>
      </Box>
      <Box className={styles.userInfo}>
        <Box className={styles.userName}>{getDisplayName(currentUser)}</Box>
        {currentOrg?.name && (
          <Box className={styles.userPosition}>
            {userPosition ? `${userPosition} at ${currentOrg.name}` : currentOrg.name}
          </Box>
        )}
      </Box>
    </Box>
  )
}

const CCRMActivityFeedPage = ({ sessionMetrics, hasSidebar = true }: CCRMActivityFeedPageProps) => {
  const presence = usePresenceStore()
  const [toastText, setToastText] = useState<string | undefined>()
  const [hasSavedEngagedSession, setHasSavedEnagagedSession] = useState<boolean>(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false)
  const currentUser = useSelector<RootState, CurrentUser | undefined>(getCurrentUser)
  const currentOrg = useSelector<RootState, any>(getCurrentOrg)
  const { activityScrollOffset, setActivityScrollOffset } = useContext(ScrollContext)
  const { loaded: filtersLoaded } = useAppSelector(({ filters }) => filters)
  const LoadingComponent = useMemo(() => driftLoadingStateBuilder('Loading'), [])

  const {
    activeSavedFilter,
    defaultFilterId,
    savedFilters,
    updateActiveFilterId,
    updateDefaultFilterId,
    createNewSavedFilter,
    updateSavedFilter,
    removeSavedFilter
  } = useSavedFilters(setToastText, false)

  const {
    showSavedFilterModal,
    editableFilterGroups,
    editableSavedFilter,
    onOpenNewModal,
    onOpenEditModal,
    onCloseModal
  } = useSavedFilterEditModal()

  useEffect(() => {
    if (toastText) {
      const timeout = setTimeout(() => setToastText(undefined), 5000)
      return () => clearTimeout(timeout)
    }
  }, [toastText])

  const onScroll = useCallback(
    (scrollOffset: number) => {
      if (scrollOffset > ENGAGED_SESSION_SCROLL_THRESHOLD && !hasSavedEngagedSession) {
        const previousEngagedSessions = parseInt(localStorage.getItem(ENGAGED_SESSIONS_KEY) || '0')
        localStorage.setItem(ENGAGED_SESSIONS_KEY, (previousEngagedSessions + 1).toString())
        setHasSavedEnagagedSession(true)

        if (previousEngagedSessions + 1 === ENGAGED_SESSION_COUNT_FEEDBACK_THRESHOLD) {
          setShowFeedbackModal(true)
        }
      }
      setActivityScrollOffset(scrollOffset)
    },
    [
      setShowFeedbackModal,
      hasSavedEngagedSession,
      setHasSavedEnagagedSession,
      setActivityScrollOffset
    ]
  )

  if (!filtersLoaded) {
    return <LoadingComponent />
  }

  return (
    <PresenceContext.Provider value={presence}>
      <Box className={styles.container}>
        {showFeedbackModal && (
          <CCRMFeedbackModal
            onClose={() => setShowFeedbackModal(false)}
            onSubmit={() =>
              setToastText('Feedback submitted - thanks for taking the time to make Drift better!')
            }
          />
        )}
        {showSavedFilterModal && (
          <SaveFilterModal
            editableGroups={editableFilterGroups}
            savedFilter={editableSavedFilter}
            defaultFilterId={defaultFilterId}
            createSavedFilter={createNewSavedFilter}
            updateSavedFilter={updateSavedFilter}
            onClose={onCloseModal}
            withDefaultFilter={false}
          />
        )}
        {toastText && <Toast text={toastText}></Toast>}
        <Box id="feedContainer" className={styles.CCRMActivityFeedPage}>
          <Box className={styles.ContentContainer}>
            <Box className={styles.LeftSideBarWrapper}>
              <Box className={styles.LeftSideBar}>
                <Box className={styles.SidebarCard}>
                  <UserCard currentUser={currentUser} currentOrg={currentOrg} />
                </Box>
                {hasSidebar && (
                  <MobileTooltipQR>
                    <Box className={styles.SidebarCard}>
                      <CCRMSavedViewsSidebar
                        savedFilters={savedFilters}
                        activeFilterId={activeSavedFilter?.id || INACTIVE_FILTER}
                        setActiveFilterId={updateActiveFilterId}
                        defaultFilterId={defaultFilterId}
                        updateDefaultFilterId={updateDefaultFilterId}
                        removeSavedFilter={removeSavedFilter}
                        onOpenEditModal={onOpenEditModal}
                        onCreateSavedFilter={onOpenNewModal}
                      />
                    </Box>
                  </MobileTooltipQR>
                )}
              </Box>
            </Box>
            <Box className={styles.CenterContainer}>
              <Box className={styles.FeedContainer}>
                <CCRMHeaderBar
                  activeFilter={activeSavedFilter}
                  setActiveFilterId={updateActiveFilterId}
                  onOpenEditModal={onOpenEditModal}
                />
                <CCRMFeed
                  scrollTargetId="feedContainer"
                  withRefresh
                  onScroll={onScroll}
                  sessionMetrics={sessionMetrics}
                  initialScrollY={activityScrollOffset}
                />
              </Box>
            </Box>
            <Box className={styles.RightSideBarWrapper}>
              <Box className={styles.RightSideBarPlaceholder} />
            </Box>
          </Box>
        </Box>
      </Box>
    </PresenceContext.Provider>
  )
}

export default withNotificationsModal(CCRMActivityFeedPage)
